import { Listbox, Switch, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { Controller, useFormContext } from "react-hook-form";
import clsx from "clsx";
import { Fragment } from "react/jsx-runtime";
import ReturnAddress from "./returnAddress";
import Divider from "../../../../components/divider";
import { countryCodes } from "../../../../constants";

export default function ContactInformation() {
  const { control, watch } = useFormContext();
  const enabled = watch("addressInfo.isReturnAddressDifferent") ?? false;

  return (
    <section>
      <div className="flex flex-col gap-2 mb-3">
        <h2 className="text-lg font-medium text-gray-950">
          Contact Information
        </h2>
      </div>

      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-7 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="block text-sm/6 font-normal text-gray-950">
              Phone number
            </label>
            <div>
              <Controller
                name="contactInfo.userPhoneNumber"
                control={control}
                rules={{
                  required: "Phone number is required",
                  validate: (value) => {
                    const length = value?.number?.length ?? 0;
                    return (
                      (length >= 6 && length <= 14) ||
                      "Must be a valid phone number"
                    );
                  },
                }}
                defaultValue={{
                  countryCode: countryCodes[0],
                  number: "",
                }}
                render={({ field, fieldState }) => {
                  const value = field.value ?? {
                    countryCode: countryCodes[0],
                    number: "",
                  };
                  return (
                    <div className="flex flex-col gap-2">
                      <div
                        className={clsx(
                          "flex rounded-xl border outline-1 -outline-offset-1 outline-white/10 focus-within:outline-2 focus-within:-outline-offset-2",
                          fieldState.error
                            ? "border-red-500 focus-within:outline-red-500"
                            : "border-gray-200 focus-within:outline-blue-500",
                        )}
                      >
                        {/* Country code */}
                        <Listbox
                          value={value.countryCode}
                          onChange={(countryCode) =>
                            field.onChange({
                              ...value,
                              countryCode,
                            })
                          }
                        >
                          <div className="relative">
                            <Listbox.Button className="flex items-center gap-1 py-3.5 pl-3 text-sm text-gray-500 focus:outline-none">
                              <span>{value.countryCode}</span>
                              <ChevronDownIcon className="size-4 text-gray-400" />
                            </Listbox.Button>

                            <Transition as={Fragment}>
                              <Listbox.Options className="absolute z-10 mt-1 w-20 h-46 overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg outline-none">
                                {countryCodes.map((code) => (
                                  <Listbox.Option
                                    key={code}
                                    value={code}
                                    className={({ active }) =>
                                      `cursor-pointer px-3 py-2 ${
                                        active
                                          ? "data-focus:bg-blue-50 data-focus:text-blue-500"
                                          : "text-black"
                                      }`
                                    }
                                  >
                                    {code}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        </Listbox>

                        {/* Phone input */}
                        <input
                          type="tel"
                          placeholder="8002608626"
                          value={value.number}
                          onChange={(e) =>
                            field.onChange({
                              ...value,
                              number: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          className="block w-full pr-4 pl-1 py-3.5 text-sm outline-none placeholder:text-gray-500"
                        />
                      </div>

                      {fieldState.error && (
                        <p className="text-sm text-red-500">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="userEmail"
              className="block text-sm/6 font-normal text-gray-950"
            >
              Email
            </label>
            <div>
              <Controller
                name="contactInfo.userEmail"
                control={control}
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Must be a valid email address",
                  },
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <input
                      {...field}
                      id="userEmail"
                      value={field.value ?? ""}
                      type="email"
                      placeholder="your.email@example.com"
                      className={clsx(
                        "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                        fieldState.error ? "border-red-500" : "border-gray-200",
                      )}
                    />
                    {fieldState.error && (
                      <p className="mt-2 text-sm text-red-500">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <Divider />

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-medium text-gray-950">
            Different return address?
          </h2>
          <p className="md:text-sm text-xs font-normal text-gray-400">
            Toggle if your return address differs from eBay address
          </p>
        </div>

        <Controller
          name="addressInfo.isReturnAddressDifferent"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={field.onChange}
              className={clsx(
                "group relative inline-flex w-18 h-9 shrink-0 cursor-pointer rounded-full p-1 transition-colors duration-200 ease-in-out outline-blue-500",
                field.value ? "bg-blue-500" : "bg-gray-200",
              )}
            >
              <span
                aria-hidden="true"
                className={clsx(
                  "pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition duration-200 ease-in-out",
                  field.value ? "translate-x-9" : "translate-x-0",
                )}
              />
            </Switch>
          )}
        />
      </div>

      {enabled && <ReturnAddress />}
    </section>
  );
}
